<template>
    <UserLayout
        :page-title="t('createSupportTicket') || 'Create Support Ticket'"
        :page-section="t('newRequest') || 'New Request'"
    >
        <div class="stc-wrap">

            <!-- Back button -->
            <button @click="router.visit('/user/support-tickets')" class="stc-back anim-up" style="--d:0ms">
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
                </svg>
                {{ t('backToTickets') || 'Back to Tickets' }}
            </button>

            <!-- Info strip -->
            <div class="stc-info anim-up" style="--d:40ms">
                <div class="stc-info-icon">
                    <svg width="22" height="22" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                </div>
                <div>
                    <div class="stc-info-title">{{ t('needHelp') || 'Need Help?' }}</div>
                    <div class="stc-info-text">{{ t('supportDescription') || 'Our support team is here to assist you. Please provide as much detail as possible about your issue so we can help you quickly and effectively.' }}</div>
                </div>
            </div>

            <!-- Create form card -->
            <div class="stc-card anim-up" style="--d:80ms">
                <div class="stc-card-title">{{ t('createNewTicket') || 'Create New Ticket' }}</div>

                <form @submit.prevent="submitTicket" class="stc-form">

                    <!-- Subject -->
                    <div class="stc-field">
                        <label for="subject" class="stc-field-label">
                            {{ t('subject') || 'Subject' }} <span class="stc-required">*</span>
                        </label>
                        <input id="subject" v-model="ticketForm.subject" type="text"
                            :placeholder="t('subjectPlaceholder') || 'Brief summary of your issue'"
                            :class="['stc-input', ticketForm.errors.subject ? 'stc-input--err' : '']"
                            required />
                        <p v-if="ticketForm.errors.subject" class="stc-err">{{ ticketForm.errors.subject }}</p>
                    </div>

                    <!-- Category -->
                    <div class="stc-field">
                        <label for="category" class="stc-field-label">{{ t('category') || 'Category' }}</label>
                        <select id="category" v-model="ticketForm.category"
                            :class="['stc-input stc-select', ticketForm.errors.category ? 'stc-input--err' : '']">
                            <option value="">{{ t('selectCategory') || 'Select a category' }}</option>
                            <option v-for="cat in categories" :key="cat.value" :value="cat.value">{{ cat.label }}</option>
                        </select>
                        <p v-if="ticketForm.errors.category" class="stc-err">{{ ticketForm.errors.category }}</p>
                    </div>

                    <!-- Priority -->
                    <div class="stc-field">
                        <label class="stc-field-label">
                            {{ t('priority') || 'Priority' }} <span class="stc-required">*</span>
                        </label>
                        <div class="stc-priority-grid">
                            <label v-for="priority in priorities" :key="priority.value"
                                class="stc-priority-opt"
                                :class="ticketForm.priority === priority.value ? 'stc-priority-opt--active' : ''"
                                :style="ticketForm.priority === priority.value ? `border-color:${priority.color};box-shadow:0 0 0 1px ${priority.color}22` : ''">
                                <input v-model="ticketForm.priority" type="radio" :value="priority.value" class="stc-sr-only" />
                                <div class="stc-priority-inner">
                                    <div class="stc-priority-dot" :style="`background:${priority.color}`"></div>
                                    <span class="stc-priority-label">{{ priority.label }}</span>
                                </div>
                                <p class="stc-priority-desc">{{ priority.description }}</p>
                                <div v-if="ticketForm.priority === priority.value" class="stc-priority-check">
                                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20" :style="`color:${priority.color}`">
                                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                                    </svg>
                                </div>
                            </label>
                        </div>
                        <p v-if="ticketForm.errors.priority" class="stc-err">{{ ticketForm.errors.priority }}</p>
                    </div>

                    <!-- Description -->
                    <div class="stc-field">
                        <label for="description" class="stc-field-label">
                            {{ t('description') || 'Description' }} <span class="stc-required">*</span>
                        </label>
                        <textarea id="description" v-model="ticketForm.description" rows="8" maxlength="5000"
                            :placeholder="t('descriptionPlaceholder') || 'Please describe your issue in detail. Include any relevant information, error messages, or steps to reproduce the problem.'"
                            :class="['stc-input stc-textarea', ticketForm.errors.description ? 'stc-input--err' : '']"
                            required></textarea>
                        <div class="stc-desc-foot">
                            <p v-if="ticketForm.errors.description" class="stc-err">{{ ticketForm.errors.description }}</p>
                            <p class="stc-char-count stc-ml-auto">{{ ticketForm.description.length }} / 5000 {{ t('characters') || 'characters' }}</p>
                        </div>
                    </div>

                    <!-- Attachments -->
                    <div class="stc-field">
                        <label class="stc-field-label">
                            {{ t('attachments') || 'Attachments' }} ({{ t('optional') || 'Optional' }})
                        </label>
                        <p class="stc-attach-hint">{{ t('attachmentHelp') || 'Upload screenshots, documents, or other files that might help us understand your issue better. Max 10MB per file.' }}</p>

                        <input ref="fileInput" type="file" multiple
                            accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.txt,.zip"
                            @change="handleFileSelect" class="stc-sr-only" />

                        <button type="button" @click="fileInput.click()" class="stc-drop-zone">
                            <svg width="36" height="36" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
                            </svg>
                            <div class="stc-drop-text">
                                <p class="stc-drop-main">{{ t('clickToUpload') || 'Click to upload files' }}</p>
                                <p class="stc-drop-sub">{{ t('fileFormats') || 'JPG, PNG, PDF, DOC, DOCX, TXT, ZIP up to 10MB' }}</p>
                            </div>
                        </button>

                        <div v-if="selectedFiles.length" class="stc-files">
                            <p class="stc-files-label">{{ t('selectedFiles') || 'Selected Files' }} ({{ selectedFiles.length }})</p>
                            <div v-for="(file, index) in selectedFiles" :key="index" class="stc-file-item">
                                <div class="stc-file-icon">
                                    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
                                    </svg>
                                </div>
                                <div class="stc-file-info">
                                    <p class="stc-file-name">{{ file.name }}</p>
                                    <p class="stc-file-size">{{ formatFileSize(file.size) }}</p>
                                </div>
                                <button type="button" @click="removeFile(index)" class="stc-file-remove">
                                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <p v-if="ticketForm.errors.attachments" class="stc-err">{{ ticketForm.errors.attachments }}</p>
                    </div>

                    <!-- Actions -->
                    <div class="stc-actions">
                        <button type="button" @click="router.visit('/user/support-tickets')" class="stc-btn-cancel">
                            {{ t('cancel') || 'Cancel' }}
                        </button>
                        <button type="submit"
                            :disabled="ticketForm.processing || !ticketForm.subject || !ticketForm.description"
                            class="stc-btn-submit">
                            <template v-if="ticketForm.processing">
                                <svg class="stc-spin" width="18" height="18" fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {{ t('creatingTicket') || 'Creating Ticket...' }}
                            </template>
                            <template v-else>
                                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
                                </svg>
                                {{ t('submitTicket') || 'Submit Ticket' }}
                            </template>
                        </button>
                    </div>
                </form>
            </div>

            <!-- FAQ card -->
            <div class="stc-card anim-up" style="--d:140ms">
                <div class="stc-card-title">{{ t('commonQuestions') || 'Common Questions' }}</div>
                <div class="stc-faq-list">
                    <div class="stc-faq-item">
                        <div class="stc-faq-q">
                            <div class="stc-faq-icon">
                                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                                </svg>
                            </div>
                            {{ t('securityQuestion') || 'Is my information secure?' }}
                        </div>
                        <p class="stc-faq-a">{{ t('securityAnswer') || 'Yes, all support tickets and attachments are encrypted and securely stored. Only authorized support staff can access your ticket.' }}</p>
                    </div>
                    <div class="stc-faq-item">
                        <div class="stc-faq-q">
                            <div class="stc-faq-icon">
                                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
                                </svg>
                            </div>
                            {{ t('notificationQuestion') || 'Will I be notified of updates?' }}
                        </div>
                        <p class="stc-faq-a">{{ t('notificationAnswer') || "Yes, you'll receive email notifications whenever there's a new reply to your ticket. You can also check your ticket status anytime." }}</p>
                    </div>
                </div>
            </div>

        </div>
    </UserLayout>
</template>

<script setup>
import {ref, computed, onMounted} from 'vue';
import {usePage, useForm, router} from '@inertiajs/vue3';
import UserLayout from "@/Layouts/UserLayout/UserLayout.vue";
import {useToast} from "@/composables/useToast.js";
import {useTranslation} from '@/composables/useTranslation';

const {showToast} = useToast();
const {t} = useTranslation();
const page = usePage();

const ticketForm = useForm({
    subject: '',
    description: '',
    category: '',
    priority: 'medium',
    attachments: []
});

const fileInput = ref(null);
const selectedFiles = ref([]);

const primaryColor = computed(() => page.props.primaryColor || '#86efac');

const hexToRgb = (hex) => {
    if (!hex || typeof hex !== 'string') {
        return {r: 134, g: 239, b: 172};
    }
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : {r: 134, g: 239, b: 172};
};

const rgbToHex = (r, g, b) => {
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

const adjustColor = (hex, amount) => {
    const rgb = hexToRgb(hex);
    const newR = Math.min(255, Math.max(0, rgb.r + amount));
    const newG = Math.min(255, Math.max(0, rgb.g + amount));
    const newB = Math.min(255, Math.max(0, rgb.b + amount));
    return rgbToHex(newR, newG, newB);
};

const themeColors = computed(() => ({
    primary: primaryColor.value,
    primaryDark: adjustColor(primaryColor.value, -20),
    primaryLight: adjustColor(primaryColor.value, 40),
    primaryRgb: `${hexToRgb(primaryColor.value).r}, ${hexToRgb(primaryColor.value).g}, ${hexToRgb(primaryColor.value).b}`
}));

const categories = [
    {value: 'account', label: t('categoryAccount') || 'Account Issues'},
    {value: 'investment', label: t('categoryInvestment') || 'Investment Questions'},
    {value: 'withdrawal', label: t('categoryWithdrawal') || 'Withdrawal Problems'},
    {value: 'deposit', label: t('categoryDeposit') || 'Deposit Issues'},
    {value: 'staking', label: t('categoryStaking') || 'Staking Questions'},
    {value: 'referral', label: t('categoryReferral') || 'Referral Program'},
    {value: 'technical', label: t('categoryTechnical') || 'Technical Support'},
    {value: 'billing', label: t('categoryBilling') || 'Billing Inquiries'},
    {value: 'kyc', label: t('categoryKyc') || 'KYC Verification'},
    {value: 'security', label: t('categorySecurity') || 'Security Concerns'},
    {value: 'general', label: t('categoryGeneral') || 'General Questions'},
    {value: 'feature', label: t('categoryFeature') || 'Feature Requests'}
];

const priorities = [
    {
        value: 'low',
        label: t('priorityLow') || 'Low',
        color: '#6b7280',
        description: t('priorityLowDescription') || 'General questions or minor issues'
    },
    {
        value: 'medium',
        label: t('priorityMedium') || 'Medium',
        color: '#3b82f6',
        description: t('priorityMediumDescription') || 'Standard support request'
    },
    {
        value: 'high',
        label: t('priorityHigh') || 'High',
        color: '#f59e0b',
        description: t('priorityHighDescription') || 'Urgent but not critical'
    },
    {
        value: 'urgent',
        label: t('priorityUrgent') || 'Urgent',
        color: '#ef4444',
        description: t('priorityUrgentDescription') || 'Critical issue requiring immediate attention'
    }
];

const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const maxSize = 10 * 1024 * 1024;
    const validFiles = [];
    let hasInvalidFiles = false;

    files.forEach(file => {
        if (file.size > maxSize) {
            showToast(`${file.name} ${t('fileTooLarge') || 'is too large. Maximum size is 10MB'}`, 'error');
            hasInvalidFiles = true;
        } else {
            validFiles.push(file);
        }
    });

    if (validFiles.length > 0) {
        selectedFiles.value = [...selectedFiles.value, ...validFiles];
        ticketForm.attachments = selectedFiles.value;
        showToast(t('filesAttached') || `${validFiles.length} file(s) attached successfully`, 'success');
    }

    // Reset file input
    if (fileInput.value) {
        fileInput.value.value = '';
    }
};

const removeFile = (index) => {
    const fileName = selectedFiles.value[index].name;
    selectedFiles.value.splice(index, 1);
    ticketForm.attachments = selectedFiles.value;
    showToast(t('fileRemoved') || `${fileName} removed`, 'info');
};

const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

const submitTicket = () => {
    // Validate required fields
    if (!ticketForm.subject || !ticketForm.subject.trim()) {
        showToast(t('subjectRequired') || 'Subject is required', 'error');
        return;
    }

    if (!ticketForm.description || !ticketForm.description.trim()) {
        showToast(t('descriptionRequired') || 'Description is required', 'error');
        return;
    }

    if (!ticketForm.category) {
        showToast(t('categoryRequired') || 'Please select a category', 'error');
        return;
    }

    const formData = new FormData();
    formData.append('subject', ticketForm.subject);
    formData.append('description', ticketForm.description);
    formData.append('category', ticketForm.category);
    formData.append('priority', ticketForm.priority);

    selectedFiles.value.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file);
    });

    router.post('/user/support-tickets', formData, {
        preserveScroll: true,
        onSuccess: () => {
            ticketForm.reset();
            selectedFiles.value = [];
            if (fileInput.value) {
                fileInput.value.value = '';
            }
            setTimeout(() => {
                router.visit('/user/support-tickets');
            }, 1500);
        },
        onError: (errors) => {
            if (errors.subject) {
                showToast(errors.subject, 'error');
            }
            if (errors.description) {
                showToast(errors.description, 'error');
            }
            if (errors.category) {
                showToast(errors.category, 'error');
            }
        }
    });
};

onMounted(() => {
    const flash = page.props.flash;
    if (flash?.success) showToast(flash.success, 'success');
    if (flash?.error) showToast(flash.error, 'error');
    if (flash?.warning) showToast(flash.warning, 'warning');
    if (flash?.info) showToast(flash.info, 'info');
});
</script>

<style scoped>
/* ── Shared animation ── */
.anim-up { animation: slideUp 0.45s cubic-bezier(0.22,1,0.36,1) both; animation-delay: var(--d,0ms); }
@keyframes slideUp { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:none} }

/* ── Page wrapper ── */
.stc-wrap {
    max-width: 960px;
    margin: 0 auto;
    padding: 14px 12px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 16px;
    position: relative;
    z-index: 10;
    width: 100%;
}
@media(min-width:640px){ .stc-wrap{ padding: 18px 18px; } }
@media(min-width:1024px){ .stc-wrap{ padding: 20px 24px; } }

/* ── Back button ── */
.stc-back {
    display: inline-flex; align-items: center; gap: 6px;
    background: none; border: none; cursor: pointer;
    color: rgba(255,255,255,0.45); font-size: 13px; font-weight: 600;
    padding: 6px 0; transition: color 0.2s; align-self: flex-start;
}
.stc-back:hover { color: #a75ded; }

/* ── Info strip ── */
.stc-info {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    background: rgba(167,93,237,0.06);
    border: 1px solid rgba(167,93,237,0.16);
    border-radius: 13px;
    padding: 14px 16px;
    position: relative;
    overflow: hidden;
}
.stc-info::before {
    content: '';
    position: absolute;
    top: 0; left: 14%; right: 14%;
    height: 1px;
    background: linear-gradient(90deg,transparent,rgba(167,93,237,0.32),transparent);
}
.stc-info-icon {
    width: 38px; height: 38px; border-radius: 10px; flex-shrink: 0;
    background: rgba(167,93,237,0.14);
    border: 1px solid rgba(167,93,237,0.22);
    display: flex; align-items: center; justify-content: center;
    color: #a75ded;
}
.stc-info-title { font-size: 13px; font-weight: 700; color: #fff; margin-bottom: 4px; }
.stc-info-text { font-size: 12px; color: rgba(255,255,255,0.5); line-height: 1.6; }

/* ── Card ── */
.stc-card {
    background: rgba(10,4,22,0.97);
    border: 1px solid rgba(167,93,237,0.13);
    border-radius: 16px;
    padding: 16px;
    position: relative;
    overflow: hidden;
}
.stc-card::before {
    content: '';
    position: absolute;
    top: 0; left: 14%; right: 14%;
    height: 1px;
    background: linear-gradient(90deg,transparent,rgba(167,93,237,0.32),transparent);
}
.stc-card-title { font-size: 13px; font-weight: 800; letter-spacing: -0.01em; color: #fff; margin-bottom: 16px; }

/* ── Form ── */
.stc-form { display: flex; flex-direction: column; gap: 16px; }
.stc-field { display: flex; flex-direction: column; gap: 0; }
.stc-field-label { font-size: 10px; font-weight: 700; color: rgba(167,93,237,0.8); text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 6px; display: block; }
.stc-required { color: #f87171; }
.stc-input {
    width: 100%; box-sizing: border-box;
    padding: 9px 12px;
    background: rgba(10,4,22,0.7);
    border: 1px solid rgba(167,93,237,0.2);
    border-radius: 9px;
    color: #fff;
    font-size: 13px; font-weight: 500;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
}
.stc-input::placeholder { color: rgba(255,255,255,0.22); }
.stc-input:focus { border-color: rgba(167,93,237,0.55); box-shadow: 0 0 0 3px rgba(167,93,237,0.1); }
.stc-input--err { border-color: rgba(248,113,113,0.5); }
.stc-select { cursor: pointer; }
.stc-select option { background: #1a0a40; color: #fff; }
.stc-textarea { resize: none; line-height: 1.6; }
.stc-err { font-size: 12px; color: #f87171; margin-top: 4px; }

/* ── Priority grid ── */
.stc-priority-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
}
@media(max-width:500px){ .stc-priority-grid{ grid-template-columns: 1fr; } }
.stc-priority-opt {
    position: relative;
    padding: 12px;
    background: rgba(10,4,22,0.6);
    border: 1px solid rgba(167,93,237,0.15);
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s;
}
.stc-priority-opt:hover { background: rgba(167,93,237,0.05); border-color: rgba(167,93,237,0.3); }
.stc-priority-opt--active { background: rgba(167,93,237,0.06); }
.stc-priority-inner { display: flex; align-items: center; gap: 7px; margin-bottom: 3px; }
.stc-priority-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.stc-priority-label { font-size: 12px; font-weight: 700; color: #fff; }
.stc-priority-desc { font-size: 10px; color: rgba(255,255,255,0.4); padding-left: 15px; }
.stc-priority-check { position: absolute; top: 10px; right: 10px; }
.stc-sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0; }

/* ── Description footer ── */
.stc-desc-foot { display: flex; align-items: flex-start; justify-content: space-between; margin-top: 4px; }
.stc-char-count { font-size: 11px; color: rgba(255,255,255,0.3); }
.stc-ml-auto { margin-left: auto; }

/* ── Attachments ── */
.stc-attach-hint { font-size: 11px; color: rgba(255,255,255,0.35); margin-bottom: 10px; }
.stc-drop-zone {
    width: 100%; box-sizing: border-box;
    padding: 28px 16px;
    background: rgba(10,4,22,0.6);
    border: 2px dashed rgba(167,93,237,0.25);
    border-radius: 12px;
    display: flex; flex-direction: column; align-items: center; gap: 12px;
    cursor: pointer; color: rgba(255,255,255,0.35);
    transition: background 0.2s, border-color 0.2s, color 0.2s;
}
.stc-drop-zone:hover { background: rgba(167,93,237,0.06); border-color: rgba(167,93,237,0.45); color: rgba(255,255,255,0.7); }
.stc-drop-text { text-align: center; }
.stc-drop-main { font-size: 14px; font-weight: 600; margin-bottom: 4px; }
.stc-drop-sub { font-size: 11px; }

.stc-files { margin-top: 12px; }
.stc-files-label { font-size: 12px; font-weight: 600; color: rgba(167,93,237,0.7); margin-bottom: 8px; }
.stc-file-item {
    display: flex; align-items: center; gap: 12px;
    padding: 10px 12px;
    background: rgba(10,4,22,0.6);
    border: 1px solid rgba(167,93,237,0.1);
    border-radius: 10px;
    margin-top: 6px;
}
.stc-file-icon {
    width: 36px; height: 36px; border-radius: 8px; flex-shrink: 0;
    background: rgba(167,93,237,0.1);
    display: flex; align-items: center; justify-content: center;
    color: #a75ded;
}
.stc-file-info { flex: 1; min-width: 0; }
.stc-file-name { font-size: 13px; font-weight: 600; color: #fff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.stc-file-size { font-size: 11px; color: rgba(255,255,255,0.35); }
.stc-file-remove {
    background: none; border: none; cursor: pointer;
    color: rgba(255,255,255,0.3); padding: 4px;
    border-radius: 6px; transition: color 0.2s, background 0.2s;
}
.stc-file-remove:hover { color: #f87171; background: rgba(248,113,113,0.08); }

/* ── Actions ── */
.stc-actions { display: flex; gap: 10px; padding-top: 4px; }
.stc-btn-cancel {
    flex: 1; padding: 10px 18px;
    background: rgba(10,4,22,0.7);
    border: 1px solid rgba(167,93,237,0.15);
    border-radius: 9px;
    color: rgba(255,255,255,0.6); font-size: 13px; font-weight: 600;
    cursor: pointer; transition: color 0.2s, background 0.2s;
}
.stc-btn-cancel:hover { color: #fff; background: rgba(167,93,237,0.08); }
.stc-btn-submit {
    flex: 1; padding: 10px 18px;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    background: linear-gradient(135deg, #a75ded, #7c3aed);
    border: none; border-radius: 9px;
    color: #fff; font-size: 13px; font-weight: 700;
    cursor: pointer; transition: opacity 0.2s, transform 0.15s;
}
.stc-btn-submit:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
.stc-btn-submit:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
.stc-spin { animation: spin 0.8s linear infinite; }
@keyframes spin { to{ transform: rotate(360deg); } }

/* ── FAQ ── */
.stc-faq-list { display: flex; flex-direction: column; gap: 8px; }
.stc-faq-item {
    padding: 12px 14px;
    background: rgba(10,4,22,0.5);
    border: 1px solid rgba(167,93,237,0.1);
    border-radius: 10px;
}
.stc-faq-q {
    display: flex; align-items: center; gap: 8px;
    font-size: 13px; font-weight: 700; color: #fff; margin-bottom: 6px;
}
.stc-faq-icon {
    width: 26px; height: 26px; border-radius: 7px; flex-shrink: 0;
    background: rgba(167,93,237,0.1);
    display: flex; align-items: center; justify-content: center;
    color: #a75ded;
}
.stc-faq-a { font-size: 12px; color: rgba(255,255,255,0.5); line-height: 1.6; padding-left: 34px; }
</style>
